/* OnSumn — WEEKLY signup section (GOAL B).
 *
 * Spec: docs/design/2026-07-30-fable5-subscribe-weekly-prompt.md (GOAL B).
 * Marco ruled placement and promise (2026-07-30, both CLOSED): homepage
 * ONLY, between EventsMapSection and Footer; the promise is ONE weekly
 * digest, Thursday morning — "what's on in the Bay for the week ahead."
 *
 * Honesty contract: there is NO send path in this repo yet. The line
 * "We haven't sent the first one yet. You'd be on it." is the entire
 * reason storing an address we cannot mail is an honest transaction —
 * byte-identical wording, Rhea signs it, tests/subscribe-section.test.js
 * pins it. ZERO digits render anywhere in this section: on an empty list
 * every audience number would be fabricated (16 CFR 465; pique G7 caps
 * pressure at zero here). No modal, no overlay — Off-Limits §9.
 *
 * Consumes POST /api/subscribe (GOAL A, collect-only): new, duplicate and
 * rate-limited all return the identical 200 — this UI cannot become an
 * address oracle because the endpoint refuses to distinguish.
 */

// Mirror of the worker-side validation (worker/subscribe.js EMAIL_RE) —
// client mirror only decides copy; the worker remains the authority.
const SUB_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Copy — no digits, no urgency, no audience claims (gated by test).
const SUB_COPY = {
  heading: 'WEEKLY',
  promise: "One email, Thursday morning — what's on in the Bay for the week ahead.",
  honest: "We haven't sent the first one yet. You'd be on it.",
  label: 'EMAIL',
  button: 'PUT ME ON',
  buttonBusy: 'SAVING…',
  done: "You're on the list for the first one.",
  invalid: "That doesn't look like an email — check it and try again.",
  error: "Couldn't save it just now. Try again in a minute.",
};

function SubscribeSection({ darkMode = true, radius = 14 }) {
  const [email, setEmail] = React.useState('');
  const [phase, setPhase] = React.useState('idle'); // idle | sending | done | invalid | error

  /* Deep-link landing. The Header writes #weekly into the URL, so that URL is
     shareable — but this section is React-rendered, and on a cold load the
     browser resolves the hash BEFORE the tree mounts, so it silently does
     nothing (measured: top=641, scrollY=0). Scrolling from inside the
     section's own mount effect is the one place the target is guaranteed to
     exist. Reduced-motion honored; runs once, and never steals scroll from a
     visitor who arrived without the hash. */
  React.useEffect(() => {
    if (typeof window === 'undefined' || window.location.hash !== '#weekly') return;
    const reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // One scroll is not enough: the surfaces ABOVE this one (hero, venue
    // rail) settle after their own data arrives, so a single mount-time jump
    // measured 549px PAST the section — worse than not scrolling at all.
    // Correct twice on a decaying schedule, and abandon instantly if the
    // visitor takes over. Never fight a user for the scroll position.
    let userTookOver = false;
    const yield_ = () => { userTookOver = true; };
    ['wheel', 'touchstart', 'keydown'].forEach((t) =>
      window.addEventListener(t, yield_, { passive: true, once: true }));

    const align = () => {
      if (userTookOver) return;
      const el = document.getElementById('weekly');
      if (!el) return;
      const off = el.getBoundingClientRect().top - 88; // 88 = scrollMarginTop
      if (Math.abs(off) < 8) return; // already parked
      window.scrollBy({ top: off, behavior: reduced ? 'auto' : 'smooth' });
    };

    const timers = [requestAnimationFrame(align), setTimeout(align, 600), setTimeout(align, 1400)];
    return () => {
      cancelAnimationFrame(timers[0]);
      timers.slice(1).forEach(clearTimeout);
      ['wheel', 'touchstart', 'keydown'].forEach((t) => window.removeEventListener(t, yield_));
    };
  }, []);

  /* Contrast (WCAG 2.2 — AA normal text 4.5:1, non-text 3:1).
   * THESE NUMBERS ARE NO LONGER LOAD-BEARING PROSE: tests/subscribe-section
   * .test.js now COMPUTES every pair below from the rendered styles via
   * lib/glass-contrast.js and fails under its floor. Until 2026-07-31 they
   * were comment-only, and two of them were wrong — see fieldBorder.
   *   dark : ink #FFFFFF on card #141414 = 18.42:1
   *          muted rgba(255,255,255,.5) on #141414 = 5.30:1
   *          button ink #0A0A0A on #FFFFFF = 19.80:1
   *   light: ink #1A1A1A on card #FFFFFF = 17.40:1
   *          muted #6E6E6E on #FFFFFF = 5.10:1
   *          button ink #FFFFFF on #0A0A0A = 19.80:1
   * Button is deliberately static black/white (not the accent var): the
   * accent is user-tweakable (index-main VALID_COLORS) and eucalyptus/fog
   * would sink a fixed ink below AA. Accent stays on :focus via the house
   * focus-double-ring (colors_and_type.css:280). */
  const surface = darkMode ? '#141414' : '#FFFFFF';
  const ink = darkMode ? '#FFFFFF' : '#1A1A1A';
  const muted = darkMode ? 'rgba(255,255,255,0.5)' : '#6E6E6E';
  const hairline = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.10)';
  /* Iris co-sign condition (PR #212, WCAG 1.4.11): the FIELD's boundary must
   * clear the 3:1 non-text floor in both themes — the card hairline above is
   * decorative, this input border is load-bearing (in light mode it is the
   * only cue for the field's extent).
   * CORRECTED 2026-07-31: the original note recorded 3.79 / 3.38 under an
   * over-the-CARD model. background-clip defaults to border-box, so the
   * element's own background paints UNDER its border — the border actually
   * composites over the FIELD FILL. Recomputed, exact float compositing:
   *   dark  rgba(255,255,255,.4) over fill #222222 → #727272
   *         = 3.73:1 vs fill, 4.32:1 vs card #141414
   *   light rgba(0,0,0,.45) over #FFFFFF → #8C8C8C = 3.35:1 (fill = card)
   * Both readings clear 3:1, so the error ran in the safe direction — but it
   * was wrong, and only a computed gate would have caught it. Now one does. */
  const fieldBorder = darkMode ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(0,0,0,0.45)';
  const btnBg = darkMode ? '#FFFFFF' : '#0A0A0A';
  const btnInk = darkMode ? '#0A0A0A' : '#FFFFFF';

  const busy = phase === 'sending';

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    const cleaned = email.trim().toLowerCase();
    if (cleaned.length > 254 || !SUB_EMAIL_RE.test(cleaned)) {
      setPhase('invalid');
      return;
    }
    setPhase('sending');
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned, source: 'homepage' }),
      });
      if (res.ok) setPhase('done');
      else if (res.status === 400) setPhase('invalid');
      else setPhase('error'); // 503 = namespace not created yet — honest error, never silence
    } catch {
      setPhase('error');
    }
  }

  const statusMessage = phase === 'done' ? SUB_COPY.done
    : phase === 'invalid' ? SUB_COPY.invalid
    : phase === 'error' ? SUB_COPY.error
    : '';

  return (
    /* id="weekly" is the Header's anchor target (Header.jsx WEEKLY link).
       scrollMarginTop clears the fixed header so an anchored jump doesn't
       park the heading underneath it. */
    <section id="weekly" aria-labelledby="weekly-heading" style={{ padding: '56px 32px', scrollMarginTop: 88 }}>
      <div data-sub-card style={{
        maxWidth: 640, margin: '0 auto',
        background: surface,
        border: hairline,
        borderRadius: radius,
        padding: '32px 28px',
      }}>
        <h2 id="weekly-heading" style={{
          fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 900,
          color: ink, textTransform: 'uppercase', letterSpacing: '0.02em',
          margin: '0 0 8px',
        }}>{SUB_COPY.heading}</h2>
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: '21px',
          color: ink, margin: '0 0 4px',
        }}>{SUB_COPY.promise}</p>
        <p data-sub-honest id="sub-honest" style={{
          fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
          color: muted, margin: '0 0 20px',
        }}>{SUB_COPY.honest}</p>

        <form noValidate onSubmit={onSubmit} style={{
          display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end',
        }}>
          <div style={{ flex: '1 1 220px' }}>
            <label htmlFor="sub-email" style={{
              display: 'block',
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: muted, marginBottom: 8,
            }}>{SUB_COPY.label}</label>
            <input
              id="sub-email"
              className="focus-double-ring"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (phase === 'invalid') setPhase('idle'); }}
              // sub-status appended (Iris): a user re-focusing the field
              // re-hears WHY it failed, not just "invalid entry".
              aria-describedby="sub-honest sub-status"
              aria-invalid={phase === 'invalid' ? 'true' : undefined}
              style={{
                width: '100%', boxSizing: 'border-box',
                // 16px: iOS zooms the page on focus below 16 — floor, not taste.
                fontSize: 16, minHeight: 44,
                fontFamily: 'var(--font-body)',
                color: ink,
                background: darkMode ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
                border: fieldBorder, borderRadius: 10,
                padding: '10px 14px',
              }}
            />
          </div>
          <button
            type="submit"
            className="focus-double-ring"
            // aria-disabled, not disabled (Iris): native disabled drops focus
            // to <body> for the send window; the busy guard in onSubmit
            // already prevents the double-submit, so keep focus and expose
            // the state instead.
            aria-disabled={busy ? 'true' : undefined}
            style={{
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: btnInk, background: btnBg,
              border: 'none', borderRadius: 10,
              minHeight: 44, padding: '10px 22px',
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >{busy ? SUB_COPY.buttonBusy : SUB_COPY.button}</button>
        </form>

        {/* Present from FIRST render so screen readers announce a change,
            never a region appearing. One reserved line; status text lands
            only on user interaction, which CLS excludes anyway (#208 class). */}
        <p role="status" aria-live="polite" data-sub-status id="sub-status" style={{
          fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
          color: phase === 'done' ? ink : muted,
          minHeight: 20, margin: '12px 0 0',
        }}>{statusMessage}</p>

        {/* Point-of-collection privacy notice (CalOPPA/CCPA — the address is
            collected right here). Quiet and inline: muted, small, no badge/dot/
            pulse and no modal (Off-Limits §9). Links to the full policy at the
            field itself. Deliberately DIGIT-FREE — the section's zero-digits
            gate (tests/subscribe-section.test.js) scans the whole document, and
            this line ships under it. Not part of the byte-pinned honest line. */}
        <p data-sub-privacy style={{
          fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: '18px',
          color: muted, margin: '10px 0 0',
        }}>
          We don't sell your info{' · '}
          <a
            href="/privacy"
            className="focus-double-ring"
            style={{ color: muted, textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
            onMouseEnter={(e) => { e.target.style.color = 'var(--international-orange)'; }}
            onMouseLeave={(e) => { e.target.style.color = muted; }}
            onFocus={(e) => { e.target.style.color = 'var(--international-orange)'; }}
            onBlur={(e) => { e.target.style.color = muted; }}
          >Privacy</a>
        </p>
      </div>
    </section>
  );
}

SubscribeSection.propTypes = {
  darkMode: PropTypes.bool,
  radius: PropTypes.number,
};

window.SubscribeSection = SubscribeSection;
